/**
 * MultiAgentRightPanels — the consolidated right-panel column for the
 * multi-agent IDE.
 *
 * COPIED from pages/dev-agent/components/rightPanels/RightPanels.tsx but TRIMMED
 * to the multi-agent surface: it renders ONLY Dependencies + Tests (the
 * dev-agent docs / summary / agents / search / impact / workspace panels are
 * dropped per the trim). It reads exclusively from useMultiAgentIdeStore
 * (through the two copied panels) and imports only ide/panels + shared utils —
 * no dev-agent code.
 *
 * Column chrome (collapse rail, "Panels" header, scroll container, colours) is
 * matched to the dev-agent RightPanels so the look is identical.
 */
import { useState, useCallback } from "react";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { RightRail } from "@/components/organisms/multi-agent/RightRail";
import { DependenciesPanel } from "./DependenciesPanel";
import { TestsPanel } from "./TestsPanel";
import { ImpactPanel } from "./ImpactPanel";
import { MultiAgentWorkspacePanel } from "./MultiAgentWorkspacePanel";
import type { MultiAgentRightPanelsProps } from "./rightPanelsTypes";

type PanelId = "workspace" | "impact" | "dependencies" | "tests";

// Workspace (the run's code files) first, then Impact (what breaks) → Tests
// (coverage) → Dependencies. Run details (RightRail) renders above these.
const DEFAULT_ORDER: PanelId[] = ["workspace", "impact", "tests", "dependencies"];

export function MultiAgentRightPanels({
  onSendMessage,
  onOpenFileInEditor,
  rightPanelWidth,
  analyzingFile,
  isBusy,
  collapsed,
  onToggleCollapse,
}: MultiAgentRightPanelsProps) {
  const [panelOrder, setPanelOrder] = useState<PanelId[]>(DEFAULT_ORDER);

  const movePanel = useCallback((panelId: PanelId, direction: "up" | "down") => {
    setPanelOrder((prev) => {
      const idx = prev.indexOf(panelId);
      if (idx < 0) return prev;
      const swap = direction === "up" ? idx - 1 : idx + 1;
      if (swap < 0 || swap >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return next;
    });
  }, []);

  if (collapsed) {
    return (
      <div className="w-10 border-l border-border flex flex-col items-center pt-2 shrink-0">
        <button
          onClick={onToggleCollapse}
          className="p-1.5 rounded hover:bg-muted/50 transition-colors"
          title="Expand panels"
        >
          <PanelLeftOpen className="w-4 h-4 text-muted-foreground rotate-180" />
        </button>
      </div>
    );
  }

  return (
    <div
      className="ma-right-context h-full min-h-0 flex flex-col shrink-0 overflow-hidden text-foreground text-xs"
      style={
        rightPanelWidth != null
          ? { width: rightPanelWidth, minWidth: 200, maxWidth: 500 }
          : undefined
      }
    >
      {/* Slim collapse control — no "Panels" label (removed per request). */}
      {onToggleCollapse && (
        <div className="ma-panel-toolbar flex items-center justify-end px-2 py-1 shrink-0 z-10">
          <button
            onClick={onToggleCollapse}
            className="p-1 rounded hover:bg-muted/50 transition-colors"
            title="Collapse panels"
          >
            <PanelLeftClose className="w-3.5 h-3.5 text-muted-foreground rotate-180" />
          </button>
        </div>
      )}
      <div className="ma-right-context__scroll flex-1 min-h-0 overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-muted-foreground/25 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-muted-foreground/45">
        {/* Run details — Status · reactive lane · Agents · tokens · timeline
            (the multi-agent run, restored above Dependencies + Tests). */}
        <div className="ma-right-context__section">
          <RightRail />
        </div>
        {panelOrder.map((panelId, panelIdx) => {
          const isFirst = panelIdx === 0;
          const isLast = panelIdx === panelOrder.length - 1;
          const up = !isFirst ? () => movePanel(panelId, "up") : undefined;
          const dn = !isLast ? () => movePanel(panelId, "down") : undefined;

          if (panelId === "workspace") {
            return (
              <MultiAgentWorkspacePanel
                key="workspace"
                disabled={isBusy}
                onMoveUp={up}
                onMoveDown={dn}
                onOpenFileInEditor={onOpenFileInEditor}
              />
            );
          }

          if (panelId === "impact") {
            return (
              <ImpactPanel
                key="impact"
                analyzingFile={analyzingFile}
                isBusy={isBusy}
                onMoveUp={up}
                onMoveDown={dn}
                onSendMessage={onSendMessage}
                onOpenFileInEditor={onOpenFileInEditor}
              />
            );
          }

          if (panelId === "dependencies") {
            return (
              <DependenciesPanel
                key="dependencies"
                disabled={isBusy}
                onMoveUp={up}
                onMoveDown={dn}
                onSendMessage={onSendMessage}
                onOpenFileInEditor={onOpenFileInEditor}
              />
            );
          }

          if (panelId === "tests") {
            return (
              <TestsPanel
                key="tests"
                analyzingFile={analyzingFile}
                isBusy={isBusy}
                onMoveUp={up}
                onMoveDown={dn}
                onSendMessage={onSendMessage}
                onOpenFileInEditor={onOpenFileInEditor}
              />
            );
          }

          return null;
        })}
      </div>
    </div>
  );
}
